/**
 * PIM Approval Management page (COMP-005 V7).
 * Lists pending approval requests for approvers to approve/deny.
 */

import { useState } from "react";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "@/components/sc/status-badge";
import { EmptyState } from "@/components/sc/empty-state";
import { GlassCard } from "@/components/sc/glass-card";
import {
  usePendingApprovals,
  useApproveRequest,
  useDenyRequest,
} from "@/hooks/use-approvals";
import type { ApprovalRequest } from "@/types";

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function timeRemaining(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Expired";
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m left`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m left`;
}

export function PimApprovalsPage() {
  const { data, isLoading } = usePendingApprovals();
  const approveMutation = useApproveRequest();
  const denyMutation = useDenyRequest();
  const [denyTarget, setDenyTarget] = useState<ApprovalRequest | null>(null);
  const [denyReason, setDenyReason] = useState("");

  const requests = data?.items ?? [];

  function handleApprove(req: ApprovalRequest) {
    approveMutation.mutate(req.id, {
      onSuccess: () => toast.success(`Approved ${req.requestedByName}'s request`),
      onError: (err) => toast.error((err as Error).message || "Failed to approve"),
    });
  }

  function handleDeny() {
    if (!denyTarget || denyReason.length < 5) return;
    denyMutation.mutate(
      { requestId: denyTarget.id, reason: denyReason },
      {
        onSuccess: () => {
          toast.success(`Denied ${denyTarget.requestedByName}'s request`);
          setDenyTarget(null);
          setDenyReason("");
        },
        onError: (err) => toast.error((err as Error).message || "Failed to deny"),
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-sc-pulse text-sc-text-muted">Loading approvals...</div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <EmptyState
        icon={<ShieldAlert size={48} />}
        heading="No pending approvals"
        description="Approval requests appear here when technicians request elevated access that requires approval."
      />
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-h2 font-display text-sc-text">
        Pending Approvals ({requests.length})
      </h2>

      {/* Deny dialog */}
      {denyTarget && (
        <GlassCard className="border-sc-danger/50">
          <h3 className="text-h3 font-display text-sc-danger mb-2">
            Deny Request
          </h3>
          <p className="text-body-sm text-sc-text mb-3">
            Deny {denyTarget.requestedByName}&apos;s {denyTarget.activationTier} activation
            for {denyTarget.customerName}?
          </p>
          <textarea
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            placeholder="Denial reason (minimum 5 characters)..."
            className="w-full px-3 py-2 rounded-btn border border-sc-border bg-sc-surface text-sc-text text-body-sm mb-3 resize-none"
            rows={2}
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleDeny}
              disabled={denyReason.length < 5 || denyMutation.isPending}
              className="px-4 py-2 rounded-btn text-body-sm font-medium bg-sc-danger text-white hover:bg-sc-danger/90 disabled:opacity-50 transition-colors"
            >
              {denyMutation.isPending ? "Denying..." : "Deny Request"}
            </button>
            <button
              onClick={() => { setDenyTarget(null); setDenyReason(""); }}
              className="px-4 py-2 rounded-btn text-body-sm text-sc-text-muted hover:text-sc-text"
            >
              Cancel
            </button>
          </div>
        </GlassCard>
      )}

      {/* Request cards */}
      <div className="grid gap-3">
        {requests.map((req) => (
          <GlassCard key={req.id}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-body-sm font-medium text-sc-text">
                    {req.requestedByName}
                  </span>
                  <StatusBadge status={req.activationTier} category="techTier" />
                  <span className="text-label text-sc-text-muted">
                    {timeRemaining(req.expiresAt)}
                  </span>
                </div>
                <p className="text-body-sm text-sc-text-muted mb-1">
                  Requesting {req.activationTier} access to{" "}
                  <span className="font-medium text-sc-text">{req.customerName}</span>
                </p>
                <div className="flex items-center gap-3 text-label text-sc-text-muted">
                  <span>
                    Approvals: {req.currentApprovals}/{req.requiredApprovals}
                  </span>
                  <span>Requested {relativeTime(req.createdAt)}</span>
                </div>
                {req.approvals.length > 0 && (
                  <div className="mt-1 text-label text-sc-text-muted">
                    Approved by:{" "}
                    {req.approvals.map((a) => a.displayName).join(", ")}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleApprove(req)}
                  disabled={approveMutation.isPending}
                  className="px-3 py-1.5 rounded-btn text-body-sm font-medium bg-sc-primary text-white hover:bg-sc-primary/90 disabled:opacity-50 transition-colors"
                >
                  Approve
                </button>
                <button
                  onClick={() => setDenyTarget(req)}
                  className="px-3 py-1.5 rounded-btn text-body-sm font-medium text-sc-danger border border-sc-danger/30 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  Deny
                </button>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );
}
